/**
 * Replaces placeholders in a template string with corresponding values from an object.
 *
 * @param {string} template - The template string containing placeholders in the format `{key}`.
 * @param {Object.<string, string|undefined>} values - An object containing key-value pairs where the key corresponds to the placeholder in the template.
 * @returns {string} - The template string with placeholders replaced by corresponding values. If a placeholder does not have a corresponding value, it remains unchanged.
 */
export const replacePlaceholders = (
  template: string,
  values: { [key: string]: string | undefined }
): string => {
  return <string>template?.replace(/\{(\w+)}/g, (_, key) => {
    return key ? values[key] ?? `{${key}}` : `{${key}}`;
  });
};

/**
 * Returns the singular or plural form of a string based on a given value.
 *
 * @param {number} value - The value to determine whether to use singular or plural form.
 * @param strings=''] - A string containing the singular and plural forms separated by '||'.
 * @returns The appropriate form (singular or plural) based on the value.
 */
export const managePlurals = (value: number, strings = ''): string => {
  if (!strings) {
    return '';
  }
  const [singular, plural] = strings.split('||');
  return value === 1 ? singular : plural || singular;
};

/**
 * Replaces the given placeholder in the initial text with the actual price
 * and makes the price bold.
 * @param initialText - The initial text containing the placeholder.
 * @param placeholder - The placeholder to be replaced.
 * @param price - The price to replace the placeholder with.
 * @param makeBold - A boolean indicating whether to make the price bold (default is false).
 * @returns The text with the placeholder replaced by the actual price.
 */
export const replacePlaceholderWithPriceAndMakeBold = (
  initialText: string,
  placeholder: string,
  price: string,
  makeBold: boolean = false
) => {
  if (!initialText || !price) return initialText;

  return initialText.replace(placeholder, makeBold ? `<b>${price}</b>` : price);
};
